import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, SongAlbumCard, DateTimePicker } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { uploadMusic } from '../../../services/index';
import { reportPageView } from '../../../services/reportLog';
import { getArtistID, getUserData, useGenreStore } from '../../../store';
import { debounce } from '../../../common/debounce';
import './index.scss';

interface BasicInfo {
  songTitle: string;
  coverArtPreview: string | null;
  featuredArtists: string[];  // Featured Artists（非必填）
  producer: string;
  genres: string[];
  moods: string[];
}

interface AdvancedInfo {
  recordLabel: string;
  publisher: string;
  isrc: string;
  explicitContent: boolean;
  pLine: string;
}

interface AudioFileInfo {
  name: string;
  size: number;
  type: string;
  url?: string;    // CDN 上传后的 URL
  md5?: string;    // 文件 MD5
}

type ReleaseType = 'now' | 'scheduled';

const ReleaseSchedule: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // 从 store 获取 genre 映射
  const genreOptions = useGenreStore((state) => state.genreOptions);
  const getGenreLabel = useCallback((id: string) => {
    const option = genreOptions.find(g => g.value === id);
    return option?.label || id;
  }, [genreOptions]);

  // 从 sessionStorage 获取各步骤数据
  const basicInfo: BasicInfo | null = useMemo(() => {
    const stored = sessionStorage.getItem('releaseBasicInfo');
    return stored ? JSON.parse(stored) : null;
  }, []);

  const advancedInfo: AdvancedInfo | null = useMemo(() => {
    const stored = sessionStorage.getItem('releaseAdvancedInfo');
    return stored ? JSON.parse(stored) : null;
  }, []);

  const audioFileInfo: AudioFileInfo | null = useMemo(() => {
    const stored = sessionStorage.getItem('releaseAudioFile');
    return stored ? JSON.parse(stored) : null;
  }, []);

  useEffect(() => {
    reportPageView('content_0016', { withArtistId: true });
  }, []);

  // 检查是否有基本信息
  useEffect(() => {
    if (!basicInfo) {
      navigate(ROUTES.releaseBasicInfo);
    }
  }, [basicInfo, navigate]);

  // 表单数据
  const [releaseType, setReleaseType] = useState<ReleaseType>('now');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [artistMessage, setArtistMessage] = useState('');

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.releaseAdvanced);
  };

  // 处理艺术家留言
  const handleMessageChange = (value: string) => {
    if (value.length <= 500) {
      setArtistMessage(value);
    }
  };

  // 表单验证
  const isFormValid = () => {
    if (!releaseType) return false;
    if (releaseType === 'scheduled' && !scheduledDate) return false;
    return true;
  };

  // 处理提交
  const submitRelease = async () => {
    if (!isFormValid() || !basicInfo) return;

    // 保存发布信息到 sessionStorage
    sessionStorage.setItem('releaseScheduleInfo', JSON.stringify({
      releaseType,
      scheduledDate: scheduledDate?.toISOString() || null,
      artistMessage,
    }));

    // 计算发布时间戳
    const publishTime = releaseType === 'scheduled' && scheduledDate
      ? Math.floor(scheduledDate.getTime() / 1000)
      : Math.floor(Date.now() / 1000);

    // Featured Artists 从表单获取
    const featuredArtists = basicInfo.featuredArtists?.filter(a => a.trim() !== '') || [];

    try {
      // 调用上传接口
      const res = await uploadMusic({
        artistID: getArtistID(),
        musicFile: audioFileInfo?.url || '',
        musicMD5: audioFileInfo?.md5 || '',
        title: basicInfo.songTitle,
        cover: basicInfo.coverArtPreview || '',
        featuredArtist: featuredArtists,
        producer: basicInfo.producer || '',
        genre: basicInfo.genres.filter(g => g).map(g => Number(g)),
        mood: basicInfo.moods.filter(m => m).map(m => Number(m)),
        label: advancedInfo?.recordLabel || '',
        publisher: advancedInfo?.publisher || '',
        isrc: advancedInfo?.isrc || '',
        explicit: advancedInfo?.explicitContent ? '1' : '0',
        pLine: advancedInfo?.pLine || '',
        note: artistMessage,
        publishTime,
      });
      
      const uploadData = (res || {}) as {
        musicID?: string;
        musicId?: string;
        music_id?: string;
      };
      const musicID = uploadData.musicID || uploadData.musicId || uploadData.music_id || '';
      if (musicID) {
        sessionStorage.setItem('releaseMusicID', musicID);
      }

      navigate(ROUTES.releaseSuccess);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const debouncedSubmit = useMemo(
    () => debounce(() => { void submitRelease(); }, 300),
    [submitRelease],
  );

  const handleSubmit = () => {
    debouncedSubmit();
  };

  useEffect(() => {
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit]);

  if (!basicInfo) {
    return null;
  }

  // 将 genre ID 映射为 label
  const validGenres = basicInfo.genres.filter(g => g.trim() !== '').map(getGenreLabel);

  return (
    <div className="release-schedule">
      {/* 页面标题 */}
      <div className="release-schedule__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Release Song</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="release-schedule__content">
        <div className="release-schedule__card">
          {/* 标题 */}
          <h2 className="release-schedule__title">Choose Your Release</h2>

          {/* 歌曲预览卡片 */}
          <div className="song-preview">
            <SongAlbumCard
              coverUrl={basicInfo.coverArtPreview || undefined}
              title={basicInfo.songTitle}
              artist={getUserData()?.name || ''}
              tags={validGenres}
            />
          </div>

          {/* 表单 */}
          <Form form={form} layout="vertical" className="release-schedule__form">
            {/* From the Artist */}
            <Form.Item 
              label={
                <div className="form-label-with-counter">
                  <span>From the Artist</span>
                  <span className="counter">{artistMessage.length}/500</span>
                </div>
              }
              className="form-item"
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-expect-error - 组件库类型定义问题 */}
              <Input.TextArea
                value={artistMessage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleMessageChange(e.target.value)}
                placeholder="Share the story behind your music, production details, or any message for your listeners..."
                rows={5}
              />
            </Form.Item>

            {/* Release 选项 */}
            <Form.Item 
              label="Release"
              required
              className="form-item"
            >
              <div className="release-options">
                {/* Release Now */}
                <div
                  className={`release-option ${releaseType === 'now' ? 'release-option--selected' : ''}`}
                  onClick={() => setReleaseType('now')}
                >
                  <div className="release-option__radio">
                    <div className={`radio-circle ${releaseType === 'now' ? 'radio-circle--checked' : ''}`}>
                      {releaseType === 'now' && <div className="radio-circle__inner" />}
                    </div>
                  </div>
                  <div className="release-option__content">
                    <span className="release-option__title">Release Now</span>
                    <span className="release-option__desc">Your music will be available immediately</span>
                  </div>
                </div>

                {/* Preview Start (Scheduled) */}
                <div
                  className={`release-option ${releaseType === 'scheduled' ? 'release-option--selected' : ''}`}
                >
                  <div 
                    className="release-option__header"
                    onClick={() => setReleaseType('scheduled')}
                  >
                    <div className="release-option__radio">
                      <div className={`radio-circle ${releaseType === 'scheduled' ? 'radio-circle--checked' : ''}`}>
                        {releaseType === 'scheduled' && <div className="radio-circle__inner" />}
                      </div>
                    </div>
                    <div className="release-option__content">
                      <span className="release-option__title">Preview Start</span>
                      <span className="release-option__desc">Schedule your release for a specific date and time</span>
                    </div>
                  </div>

                  {/* 日期选择器 */}
                  {releaseType === 'scheduled' && (
                    <div className="release-option__datepicker">
                      <DateTimePicker
                        value={scheduledDate}
                        onChange={setScheduledDate}
                        placeholder="Select time"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Form.Item>

            {/* 提交按钮 */}
            <Button
              type="primary"
              disabled={!isFormValid()}
              onClick={handleSubmit}
              className={`release-schedule__submit ${!isFormValid() ? 'release-schedule__submit--disabled' : ''}`}
            >
              Submit Release
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ReleaseSchedule;
